import { supabase } from './supabase'
import type { Business, Emirate, Service, Language, PaymentMethod, BusinessHours, BusinessService, BusinessSocialLink, BusinessLanguage, BusinessPaymentMethod } from './types'

// -- Users (admin) --

export interface AdminUser {
  user_id: string
  email: string
  role: string
  created_at: string
  last_sign_in: string | null
  email_confirmed: boolean
}

export async function fetchUsers(): Promise<AdminUser[]> {
  const { data, error } = await supabase.rpc('list_users')
  if (error) throw error
  return data ?? []
}

export async function adminCreateUser(email: string, password: string, role: string = 'owner'): Promise<string> {
  const { data, error } = await supabase.rpc('create_new_user', { user_email: email, user_password: password, user_role: role })
  if (error) throw error
  return data
}

export async function adminUpdateUserRole(userId: string, newRole: string) {
  const { error } = await supabase.rpc('update_user_role', { target_user_id: userId, new_role: newRole })
  if (error) throw error
}

export async function adminDeleteUser(userId: string) {
  const { error } = await supabase.rpc('delete_user_by_admin', { target_user_id: userId })
  if (error) throw error
}

// -- Languages --

export async function fetchLanguages(): Promise<Language[]> {
  const { data, error } = await supabase.from('languages').select('*').order('name')
  if (error) throw error
  return data
}

export async function createLanguage(lang: { name: string }) {
  const { error } = await supabase.from('languages').insert(lang)
  if (error) throw error
}

export async function updateLanguage(id: string, updates: Partial<Language>) {
  const { error } = await supabase.from('languages').update(updates).eq('id', id)
  if (error) throw error
}

export async function deleteLanguage(id: string) {
  const { error } = await supabase.from('languages').delete().eq('id', id)
  if (error) throw error
}

// -- Payment Methods --

export async function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  const { data, error } = await supabase.from('payment_methods').select('*').order('name')
  if (error) throw error
  return data
}

export async function createPaymentMethod(pm: { name: string }) {
  const { error } = await supabase.from('payment_methods').insert(pm)
  if (error) throw error
}

export async function updatePaymentMethod(id: string, updates: Partial<PaymentMethod>) {
  const { error } = await supabase.from('payment_methods').update(updates).eq('id', id)
  if (error) throw error
}

export async function deletePaymentMethod(id: string) {
  const { error } = await supabase.from('payment_methods').delete().eq('id', id)
  if (error) throw error
}

// -- Services --

export async function fetchServices(): Promise<Service[]> {
  const { data, error } = await supabase.from('services').select('*').order('name_en')
  if (error) throw error
  return data
}

export async function createService(service: { name_en: string; name_ar?: string; slug: string }) {
  const { error } = await supabase.from('services').insert(service)
  if (error) throw error
}

export async function updateService(id: string, updates: Partial<Service>) {
  const { error } = await supabase.from('services').update(updates).eq('id', id)
  if (error) throw error
}

export async function deleteService(id: string) {
  const { error } = await supabase.from('services').delete().eq('id', id)
  if (error) throw error
}

// -- Emirates --

export async function fetchEmirates(): Promise<Emirate[]> {
  const { data, error } = await supabase.from('emirates').select('*').order('name_en')
  if (error) throw error
  return data
}

export async function updateEmirate(id: string, updates: Partial<Emirate>) {
  const { error } = await supabase.from('emirates').update(updates).eq('id', id)
  if (error) throw error
}

export async function createEmirate(emirate: Omit<Emirate, 'id' | 'created_at'>) {
  const { error } = await supabase.from('emirates').insert(emirate)
  if (error) throw error
}

export async function deleteEmirate(id: string) {
  const { error } = await supabase.from('emirates').delete().eq('id', id)
  if (error) throw error
}

// -- Businesses --

export async function fetchBusinesses(params?: {
  search?: string
  emirate_id?: string
  page?: number
  pageSize?: number
}): Promise<{ data: Business[]; count: number }> {
  const page = params?.page ?? 1
  const pageSize = params?.pageSize ?? 25

  let query = supabase
    .from('businesses')
    .select('*, emirate:emirates(id, name_en, slug)', { count: 'exact' })

  if (params?.search) {
    query = query.or(`name_en.ilike.%${params.search}%,phone.ilike.%${params.search}%,address_en.ilike.%${params.search}%`)
  }
  if (params?.emirate_id) {
    query = query.eq('emirate_id', params.emirate_id)
  }

  const { data, error, count } = await query
    .order('name_en')
    .range((page - 1) * pageSize, page * pageSize - 1)

  if (error) throw error
  return { data: data ?? [], count: count ?? 0 }
}

export async function fetchBusiness(id: string): Promise<Business> {
  const { data, error } = await supabase
    .from('businesses')
    .select(
      `*,
      emirate:emirates(id, name_en, slug),
      business_hours(*),
      business_services(*),
      business_social_links(*),
      business_languages(*),
      business_payment_methods(*)`
    )
    .eq('id', id)
    .single()

  if (error) throw error
  return data
}

export async function createBusiness(business: Omit<Business, 'id' | 'created_at' | 'updated_at' | 'emirate'>) {
  const { data, error } = await supabase.from('businesses').insert(business).select('id').single()
  if (error) throw error
  return data.id
}

export async function updateBusiness(id: string, updates: Partial<Business>) {
  const { error } = await supabase.from('businesses').update(updates).eq('id', id)
  if (error) throw error
}

export async function deleteBusiness(id: string) {
  const { error } = await supabase.from('businesses').delete().eq('id', id)
  if (error) throw error
}

// -- Business Hours --

export async function saveBusinessHours(businessId: string, hours: BusinessHours[]) {
  await supabase.from('business_hours').delete().eq('business_id', businessId)
  if (hours.length > 0) {
    const { error } = await supabase
      .from('business_hours')
      .insert(hours.map(h => ({ ...h, business_id: businessId, id: undefined })))
    if (error) throw error
  }
}

// -- Business Services --

export async function saveBusinessServices(businessId: string, services: { name_en: string; name_ar?: string | null; service_id?: string }[]) {
  await supabase.from('business_services').delete().eq('business_id', businessId)
  if (services.length > 0) {
    const { error } = await supabase
      .from('business_services')
      .insert(services.map(s => ({ name_en: s.name_en, name_ar: s.name_ar, service_id: s.service_id, business_id: businessId })))
    if (error) throw error
  }
}

// -- Business Social Links --

export async function saveBusinessSocialLinks(businessId: string, links: { platform: string; url: string }[]) {
  await supabase.from('business_social_links').delete().eq('business_id', businessId)
  if (links.length > 0) {
    const { error } = await supabase
      .from('business_social_links')
      .insert(links.map(l => ({ ...l, business_id: businessId })))
    if (error) throw error
  }
}

// -- Business Languages --

export async function saveBusinessLanguages(businessId: string, languages: { language: string; language_id?: string }[]) {
  await supabase.from('business_languages').delete().eq('business_id', businessId)
  if (languages.length > 0) {
    const { error } = await supabase
      .from('business_languages')
      .insert(languages.map(l => ({ language: l.language, language_id: l.language_id, business_id: businessId })))
    if (error) throw error
  }
}

// -- Business Payment Methods --

export async function saveBusinessPaymentMethods(businessId: string, methods: { method: string; method_id?: string }[]) {
  await supabase.from('business_payment_methods').delete().eq('business_id', businessId)
  if (methods.length > 0) {
    const { error } = await supabase
      .from('business_payment_methods')
      .insert(methods.map(m => ({ method: m.method, method_id: m.method_id, business_id: businessId })))
    if (error) throw error
  }
}

// -- Dashboard Stats --

export async function fetchDashboardStats(): Promise<{
  totalBusinesses: number
  activeBusinesses: number
  totalEmirates: number
  totalReviews: number
}> {
  const { count: total } = await supabase.from('businesses').select('id', { count: 'exact', head: true })
  const { count: active } = await supabase.from('businesses').select('id', { count: 'exact', head: true }).eq('is_active', true)
  const { count: emirates } = await supabase.from('emirates').select('id', { count: 'exact', head: true })
  const { data: reviews } = await supabase.from('businesses').select('review_count')

  return {
    totalBusinesses: total ?? 0,
    activeBusinesses: active ?? 0,
    totalEmirates: emirates ?? 0,
    totalReviews: (reviews ?? []).reduce((sum, b) => sum + (b.review_count ?? 0), 0),
  }
}
